"use strict";
/*******************************************************************************

    The class that defines the header of a block.

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockHeader = void 0;
var Hash_1 = require("../common/Hash");
var Height_1 = require("../common/Height");
var Signature_1 = require("../common/Signature");
var JSONValidator_1 = require("../utils/JSONValidator");
var Utils_1 = require("../utils/Utils");
var VarInt_1 = require("../utils/VarInt");
var BitMask_1 = require("./BitMask");
var Enrollment_1 = require("./Enrollment");
/**
 * The class that defines the header of a block.
 * Convert JSON object to TypeScript's instance.
 * An exception occurs if the required property is not present.
 */
var BlockHeader = /** @class */ (function () {
    /**
     * Constructor
     * @param prev_block  The Hash of the previous block in the chain of blocks
     * @param merkle_root The hash of the merkle root of the transactions
     * @param signature   The Schnorr multisig of all validators which signed this block
     * @param validators  The bit-field containing the validators' key indices which signed the block
     * @param height      The block height
     * @param preimages   The pre-images propagated in this block
     * @param enrollments The enrolled validators
     */
    function BlockHeader(prev_block, merkle_root, signature, validators, height, preimages, enrollments) {
        /**
         * Block seconds offset from Genesis Timestamp in `ConsensusParams`
         */
        this.time_offset = 0;
        this.prev_block = prev_block;
        this.merkle_root = merkle_root;
        this.signature = signature;
        this.validators = validators;
        this.height = height;
        this.preimages = preimages;
        this.enrollments = enrollments;
    }
    /**
     * The reviver parameter to give to `JSON.parse`
     *
     * This function allows to perform any necessary conversion,
     * as well as validation of the final object.
     *
     * @param key   Name of the field being parsed
     * @param value The value associated with `key`
     * @returns A new instance of `BlockHeader` if `key == ""`, `value` otherwise.
     */
    BlockHeader.reviver = function (key, value) {
        if (key !== "")
            return value;
        JSONValidator_1.JSONValidator.isValidOtherwiseThrow("BlockHeader", value);
        return new BlockHeader(new Hash_1.Hash(value.prev_block), new Hash_1.Hash(value.merkle_root), new Signature_1.Signature(value.signature), BitMask_1.BitMask.fromString(value.validators), new Height_1.Height(value.height), value.preimages.map(function (elem) { return new Hash_1.Hash(elem); }), value.enrollments.map(function (elem) { return Enrollment_1.Enrollment.reviver("", elem); }));
    };
    /**
     * Converts this object to its JSON representation
     */
    BlockHeader.prototype.toJSON = function () {
        return {
            prev_block: this.prev_block,
            merkle_root: this.merkle_root,
            signature: this.signature.toString(),
            validators: this.validators.toString(),
            height: this.height.toString(),
            preimages: this.preimages,
            enrollments: this.enrollments,
        };
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    BlockHeader.prototype.computeHash = function (buffer) {
        this.prev_block.computeHash(buffer);
        this.merkle_root.computeHash(buffer);
        this.height.computeHash(buffer);
        for (var _i = 0, _a = this.preimages; _i < _a.length; _i++) {
            var elem = _a[_i];
            Hash_1.hashPart(elem, buffer);
        }
        for (var _b = 0, _c = this.enrollments; _b < _c.length; _b++) {
            var elem = _c[_b];
            elem.computeHash(buffer);
        }
    };
    /**
     * Serialize as binary data.
     * @param buffer - The buffer where serialized data is stored
     */
    BlockHeader.prototype.serialize = function (buffer) {
        this.prev_block.serialize(buffer);
        this.merkle_root.serialize(buffer);
        this.signature.serialize(buffer);
        this.validators.serialize(buffer);
        this.height.serialize(buffer);
        VarInt_1.VarInt.fromNumber(this.preimages.length, buffer);
        for (var _i = 0, _a = this.preimages; _i < _a.length; _i++) {
            var elem = _a[_i];
            elem.serialize(buffer);
        }
        VarInt_1.VarInt.fromNumber(this.enrollments.length, buffer);
        for (var _b = 0, _c = this.enrollments; _b < _c.length; _b++) {
            var elem = _c[_b];
            elem.serialize(buffer);
        }
    };
    /**
     * Deserialize as binary data.
     * @param buffer - The buffer to be deserialized
     */
    BlockHeader.deserialize = function (buffer) {
        var prev_block = Hash_1.Hash.deserialize(buffer);
        var merkle_root = Hash_1.Hash.deserialize(buffer);
        var signature = Signature_1.Signature.deserialize(buffer);
        var validators = BitMask_1.BitMask.deserialize(buffer);
        var height = Height_1.Height.deserialize(buffer);
        var preimages_length = VarInt_1.VarInt.toNumber(buffer);
        var preimages = Utils_1.iota(preimages_length).map(function () { return Hash_1.Hash.deserialize(buffer); });
        var enroll_length = VarInt_1.VarInt.toNumber(buffer);
        var enrollments = Utils_1.iota(enroll_length).map(function () { return Enrollment_1.Enrollment.deserialize(buffer); });
        return new BlockHeader(prev_block, merkle_root, signature, validators, height, preimages, enrollments);
    };
    /**
     * Returns the data size.
     */
    BlockHeader.prototype.getNumberOfBytes = function () {
        var num_bytes = this.prev_block.getNumberOfBytes() +
            this.merkle_root.getNumberOfBytes() +
            this.signature.getNumberOfBytes() +
            this.validators.getNumberOfBytes() +
            this.height.getNumberOfBytes();
        for (var _i = 0, _a = this.preimages; _i < _a.length; _i++) {
            var elem = _a[_i];
            num_bytes += elem.getNumberOfBytes();
        }
        for (var _b = 0, _c = this.enrollments; _b < _c.length; _b++) {
            var elem = _c[_b];
            num_bytes += elem.getNumberOfBytes();
        }
        return num_bytes;
    };
    return BlockHeader;
}());
exports.BlockHeader = BlockHeader;
//# sourceMappingURL=BlockHeader.js.map